// Iter156 · Modal para aceptar una oportunidad Mercado y crear una
// ofertas_comercial en el pipeline de Licitaciones. Se abre desde la ficha
// de MercadoView con datos precargados y permite al usuario:
//   1. Elegir el estado destino (Publicada / Por decidir / En estudio / Borrador).
//   2. Editar los campos clave (cliente, DG, actividad, presupuesto, fecha,
//      responsables, incumbent, observaciones) antes de guardar.
//   3. Guardar → POST /mercado/{mid}/accept, que crea la oferta_comercial
//      vinculada y marca la mercado_opp como "accepted".
//
// El objetivo es NO ampliar OfertaForm.jsx (que ya es enorme) y mantener
// aislado el flujo import-desde-Mercado.
import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Check, Loader2, ArrowRight, Globe, Sparkles, History } from "lucide-react";
import { API } from "@/context/AuthContext";
import { ACTIVIDADES_POR_DG } from "@/pages/ofertas_comercial/shared";

export default function AcceptFromMercadoModal({ opportunity, onClose, onAccepted }) {
  const [f, setF] = useState({
    estado_licitacion: "ESTUDIO",
    cliente: opportunity?.cliente || "",
    direccion_general: (opportunity?.direccion_general || "SERVICIOS").toUpperCase(),
    actividad_comercial: opportunity?.actividad_informe_comercial || "C&W",
    tipo_contratacion: opportunity?.tipo_contratacion || "NC",
    objeto_contrato: opportunity?.objeto_contrato || "",
    presupuesto: opportunity?.presupuesto || 0,
    fecha_presentacion: opportunity?.fecha_presentacion || "",
    provincia: opportunity?.provincia || "",
    incumbent: opportunity?.incumbent || "",
    responsables: "",
    observaciones: `Aceptada desde MERCADO · CPV ${opportunity?.cpv || "—"} · Score Pando ${opportunity?.ai_score ?? "?"}`,
  });
  const [saving, setSaving] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [suggestion, setSuggestion] = useState(null); // { source, rationale }
  const [historyOpen, setHistoryOpen] = useState(false);
  const history = opportunity?.pando_suggestions_history || [];
  const update = (k, v) => setF(prev => ({ ...prev, [k]: v }));

  // Iter157 · Sugerencias Pando (Claude Haiku 4.5 vía Emergent LLM Key) para
  // prellenar DG / actividad / tipo_contratacion / presupuesto. El usuario
  // sigue siendo quien confirma el submit; esto solo ahorra clasificación.
  const suggest = async () => {
    setSuggesting(true);
    try {
      const { data } = await axios.post(`${API}/mercado/${opportunity.id}/accept-suggestions`);
      const dg = (data.direccion_general || f.direccion_general).toUpperCase();
      const validActs = ACTIVIDADES_POR_DG[dg] || [];
      const act = validActs.includes(data.actividad_comercial) ? data.actividad_comercial : (validActs[0] || f.actividad_comercial);
      const tipo = ["R", "NC", "ADC"].includes(data.tipo_contratacion) ? data.tipo_contratacion : f.tipo_contratacion;
      setF(prev => ({
        ...prev,
        direccion_general: dg,
        actividad_comercial: act,
        tipo_contratacion: tipo,
        presupuesto: prev.presupuesto || Number(data.presupuesto_estimado) || 0,
      }));
      setSuggestion({ source: data.source, rationale: data.rationale || "" });
      toast.success(data.source === "ai" ? "Sugerencias Pando aplicadas — revisa antes de guardar" : "Sugerencias heurísticas aplicadas");
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudieron obtener sugerencias");
    } finally { setSuggesting(false); }
  };

  const save = async () => {
    if (!f.cliente.trim()) { toast.error("Cliente obligatorio"); return; }
    if (!f.objeto_contrato.trim()) { toast.error("Objeto del contrato obligatorio"); return; }
    setSaving(true);
    try {
      const r = await axios.post(`${API}/mercado/${opportunity.id}/accept`, f);
      toast.success(`Licitación creada · ${r.data.expediente} · estado ${r.data.estado}`);
      onAccepted?.(r.data);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo aceptar la oportunidad");
    } finally { setSaving(false); }
  };

  if (!opportunity) return null;
  const actividadOpts = ACTIVIDADES_POR_DG[f.direccion_general] || [];

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4" onClick={onClose} data-testid="accept-mercado-modal">
      <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] flex flex-col" onClick={e=>e.stopPropagation()}>
        <div className="p-4 border-b border-gray-100 bg-gradient-to-r from-emerald-50 to-blue-50 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-base font-bold flex items-center gap-2 text-emerald-900">
                <Globe size={16} className="text-emerald-700"/>
                Aceptar oportunidad MERCADO → Licitaciones
              </h3>
              <p className="text-[11px] text-emerald-700 mt-0.5">
                Selecciona el estado destino y revisa los datos importados de Mercado. Puedes editar cualquier campo antes de crear la licitación.
              </p>
            </div>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-800" data-testid="accept-mercado-close"><X size={18}/></button>
          </div>
          <div className="mt-2 flex items-center justify-between gap-2">
            <button
              type="button"
              onClick={suggest}
              disabled={suggesting}
              data-testid="accept-mercado-suggest-ai"
              className="text-[11px] font-semibold inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-white border border-indigo-300 text-indigo-700 hover:bg-indigo-50 disabled:opacity-50">
              {suggesting ? <Loader2 size={11} className="animate-spin"/> : <Sparkles size={11}/>}
              {suggesting ? "Analizando…" : "Sugerir con Pando"}
            </button>
            {suggestion && (
              <div
                data-testid="accept-mercado-suggestion-panel"
                className={`text-[10px] px-2 py-1 rounded border flex-1 truncate ${
                  suggestion.source === "ai"
                    ? "bg-indigo-50 border-indigo-200 text-indigo-800"
                    : "bg-amber-50 border-amber-200 text-amber-800"
                }`}
                title={suggestion.rationale}>
                <span className="font-bold uppercase mr-1">
                  {suggestion.source === "ai" ? "Pando" : "Heurística"}:
                </span>
                {suggestion.rationale || "Sugerencia aplicada"}
              </div>
            )}
            {history.length > 0 && (
              <button
                type="button"
                onClick={() => setHistoryOpen(o => !o)}
                data-testid="accept-mercado-history-toggle"
                className="text-[10px] font-semibold inline-flex items-center gap-1 px-2 py-1 rounded border border-gray-300 text-gray-700 bg-white hover:bg-gray-50">
                <History size={10}/> {historyOpen ? "Ocultar" : "Historial"} ({history.length})
              </button>
            )}
          </div>
          {historyOpen && history.length > 0 && (
            <div
              data-testid="accept-mercado-history-panel"
              className="mt-2 max-h-40 overflow-y-auto border border-gray-200 rounded-md bg-white/70 divide-y divide-gray-100">
              {[...history].reverse().map((h, idx) => (
                <div key={idx} className="px-2 py-1.5 text-[10px] text-gray-700 flex flex-wrap gap-x-2 gap-y-0.5" data-testid={`accept-mercado-history-entry-${idx}`}>
                  <span className="font-mono text-gray-400">{(h.at || "").slice(0, 16).replace("T", " ")}</span>
                  <span className={`font-bold uppercase ${h.source === "ai" ? "text-indigo-700" : "text-amber-700"}`}>{h.source === "ai" ? "Pando" : "Heur"}</span>
                  <span>DG: <b>{h.direccion_general}</b></span>
                  <span>Act: <b>{h.actividad_comercial}</b></span>
                  <span>Tipo: <b>{h.tipo_contratacion}</b></span>
                  {h.presupuesto_estimado ? <span>€ <b>{Number(h.presupuesto_estimado).toLocaleString("es-ES")}</b></span> : null}
                  {h.rationale && <span className="w-full text-gray-500 italic truncate" title={h.rationale}>{h.rationale}</span>}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="p-4 overflow-y-auto flex-1 space-y-3">
          {/* Estado destino */}
          <div>
            <label className="block text-[10px] font-bold uppercase text-gray-500 tracking-wide mb-1">Estado destino en Licitaciones *</label>
            <div className="flex gap-1.5 flex-wrap">
              {["ESTUDIO", "PUBLICADA", "POR DECIDIR", "BORRADOR"].map(est => (
                <button
                  key={est}
                  type="button"
                  onClick={() => update("estado_licitacion", est)}
                  data-testid={`accept-mercado-estado-${est.replace(/\s/g, "_")}`}
                  className={`text-xs px-3 py-1.5 rounded-full font-semibold transition ${
                    f.estado_licitacion === est
                      ? "bg-emerald-600 text-white shadow-sm"
                      : "bg-white border border-gray-300 text-gray-700 hover:border-emerald-400"
                  }`}>
                  {est}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 text-xs">
            <F label="Cliente *" span={2}>
              <input value={f.cliente} onChange={e=>update("cliente", e.target.value)}
                     className="w-full border border-gray-200 rounded px-2 py-1.5" data-testid="accept-mercado-cliente"/>
            </F>
            <F label="Objeto del contrato *" span={2}>
              <textarea value={f.objeto_contrato} onChange={e=>update("objeto_contrato", e.target.value)}
                        rows={2}
                        className="w-full border border-gray-200 rounded px-2 py-1.5" data-testid="accept-mercado-objeto"/>
            </F>
            <F label="Dirección General">
              <select value={f.direccion_general}
                      onChange={e=>update("direccion_general", e.target.value)}
                      className="w-full border border-gray-200 rounded px-2 py-1.5 bg-white" data-testid="accept-mercado-dg">
                <option value="SERVICIOS">SERVICIOS</option>
                <option value="TRATAMIENTO">TRATAMIENTO</option>
              </select>
            </F>
            <F label="Actividad">
              <select value={f.actividad_comercial}
                      onChange={e=>update("actividad_comercial", e.target.value)}
                      className="w-full border border-gray-200 rounded px-2 py-1.5 bg-white" data-testid="accept-mercado-actividad">
                {actividadOpts.map(a => <option key={a} value={a}>{a}</option>)}
              </select>
            </F>
            <F label="Tipo contratación">
              <select value={f.tipo_contratacion}
                      onChange={e=>update("tipo_contratacion", e.target.value)}
                      className="w-full border border-gray-200 rounded px-2 py-1.5 bg-white" data-testid="accept-mercado-tipo-contratacion">
                <option value="NC">NC · Nueva Contratación</option>
                <option value="R">R · Renovación</option>
                <option value="ADC">ADC · Adicional</option>
              </select>
            </F>
            <F label="Presupuesto (€)">
              <input type="number" value={f.presupuesto || ""} onChange={e=>update("presupuesto", parseFloat(e.target.value) || 0)}
                     className="w-full border border-gray-200 rounded px-2 py-1.5 text-right" data-testid="accept-mercado-presupuesto"/>
            </F>
            <F label="Fecha presentación">
              <input type="date" value={f.fecha_presentacion || ""} onChange={e=>update("fecha_presentacion", e.target.value)}
                     className="w-full border border-gray-200 rounded px-2 py-1.5" data-testid="accept-mercado-fecha"/>
            </F>
            <F label="Provincia">
              <input value={f.provincia} onChange={e=>update("provincia", e.target.value)}
                     className="w-full border border-gray-200 rounded px-2 py-1.5" data-testid="accept-mercado-provincia"/>
            </F>
            <F label="Incumbent">
              <input value={f.incumbent} onChange={e=>update("incumbent", e.target.value)}
                     placeholder="Ej: VSM, ACSA, empresa actual…"
                     className="w-full border border-gray-200 rounded px-2 py-1.5" data-testid="accept-mercado-incumbent"/>
            </F>
            <F label="Responsables (email)" span={2}>
              <input value={f.responsables} onChange={e=>update("responsables", e.target.value)}
                     placeholder="Emails separados por comas — vacío = tú"
                     className="w-full border border-gray-200 rounded px-2 py-1.5" data-testid="accept-mercado-responsables"/>
            </F>
            <F label="Observaciones" span={2}>
              <textarea value={f.observaciones} onChange={e=>update("observaciones", e.target.value)}
                        rows={2}
                        className="w-full border border-gray-200 rounded px-2 py-1.5 text-[11px] text-gray-700" data-testid="accept-mercado-observaciones"/>
            </F>
          </div>
        </div>

        <div className="p-3 border-t border-gray-100 bg-gray-50 flex justify-between items-center rounded-b-2xl">
          <p className="text-[10px] text-gray-500">La oportunidad quedará marcada como aceptada y no volverá a aparecer en el radar.</p>
          <div className="flex gap-2">
            <button onClick={onClose} className="text-xs border border-gray-300 px-3 py-1.5 rounded hover:bg-gray-100">Cancelar</button>
            <button onClick={save} disabled={saving}
                    className="text-xs bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded font-semibold inline-flex items-center gap-1.5 disabled:opacity-50"
                    data-testid="accept-mercado-save">
              {saving ? <><Loader2 size={11} className="animate-spin"/>Creando…</> : <><Check size={12}/>Aceptar y crear licitación <ArrowRight size={11}/></>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function F({ label, span = 1, children }) {
  return (
    <div className={span === 2 ? "col-span-2" : ""}>
      <label className="block text-[10px] font-bold uppercase text-gray-500 tracking-wide mb-1">{label}</label>
      {children}
    </div>
  );
}
